import React, { type KeyboardEvent } from 'react';

import { injectIntl, type WrappedComponentProps } from 'react-intl-next';

import { type ActivityItem, type ActivityProvider } from '@atlaskit/activity-provider';

import { INPUT_METHOD } from '../../analytics';
import type { Providers } from '../../provider-factory';
import type { Diff } from '../../utils';


import LinkSearchList from './LinkSearchList';
import { type RecentSearchProps, type RecentSearchState } from './types';
import withActivityProvider, {
	type ExpandedActivityProviderProps,
	type WithActivityProviderProps,
} from './withActivityProvider';

const DEFAULT_ITEMS_LIMIT = 5;

const limit = (items: Array<ActivityItem>, max: number) => {
	return items.slice(0, max);
};

// Ignored via go/ees005
// eslint-disable-next-line @repo/internal/react/no-class-components
class RecentLink extends React.Component<
	RecentSearchProps & WithActivityProviderProps & WrappedComponentProps,
	RecentSearchState
> {
	private activityProvider: ActivityProvider | null = null;

	constructor(props: RecentSearchProps & WithActivityProviderProps & WrappedComponentProps) {
		super(props);
		this.state = {
			selectedIndex: -1,
			isLoading: false,
			items: [],
			url: props.defaultUrl || '',
		};
	}
	static defaultProps: Partial<RecentSearchProps> = {
		limit: DEFAULT_ITEMS_LIMIT,
	};

	componentDidUpdate(prevProps: RecentSearchProps) {
		if (prevProps.defaultUrl !== this.props.defaultUrl) {
			this.setState((state: RecentSearchState) => {
				if (state.url !== this.props.defaultUrl) {
					return {
						items: [],
						selectedIndex: -1,
						url: this.props.defaultUrl || '',
					};
				}
				return null;
			});
		}
	}

	async componentDidMount() {
		if (this.props.activityProvider) {
			this.activityProvider = await this.props.activityProvider;
			await this.loadRecentItems(this.activityProvider);
		}
	}

	private async loadRecentItems(activityProvider: ActivityProvider) {
		try {
			if (!this.state.url) {
				this.setState({
					isLoading: true,
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					items: limit(await activityProvider.getRecentItems(), this.props.limit!), // defaultProps assure that limit always contains a value
				});
			}
		} finally {
			this.setState({ isLoading: false });
		}
	}

	private getCurrentInputMethod(): INPUT_METHOD.MANUAL | INPUT_METHOD.TYPEAHEAD | undefined {
		const { items, url, selectedIndex } = this.state;

		if (items && items.length > 0 && selectedIndex > -1) {
			return INPUT_METHOD.TYPEAHEAD;
		} else if (url && url.length > 0) {
			return INPUT_METHOD.MANUAL;
		}
		return;
	}

	private handleSubmit = () => {
		const { items, url, selectedIndex } = this.state;
		const inputMethod = this.getCurrentInputMethod();

		if (!inputMethod) {
			return; // No call submit, if there is nothing to submit
		}

		switch (inputMethod) {
			case INPUT_METHOD.MANUAL: {
				this.props.onSubmit({
					url,
					text: url,
					inputMethod: INPUT_METHOD.MANUAL,
				});
				break;
			}
			case INPUT_METHOD.TYPEAHEAD: {
				const item = items[selectedIndex];
				this.setState(() => ({
					url: item.url,
				}));
				if (this.props.onSubmit) {
					this.props.onSubmit({
						url: item.url,
						text: item.name,
						inputMethod: INPUT_METHOD.TYPEAHEAD,
					});
				}
				break;
			}
		}
	};

	private handleSelected = (href: string, text: string) => {
		if (this.props.onSubmit) {
			this.setState(() => ({
				url: href,
			}));
			this.props.onSubmit({
				text,
				url: href,
				inputMethod: INPUT_METHOD.TYPEAHEAD,
			});
		}
	};

	private handleChange = async (input: string) => {
		this.setState({ url: input });

		if (this.activityProvider) {
			if (input.length === 0) {
				this.setState({
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					items: limit(await this.activityProvider.getRecentItems(), this.props.limit!),
					selectedIndex: -1,
				});
			} else {
				this.setState({
					// Ignored via go/ees005
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					items: limit(await this.activityProvider.searchRecent(input), this.props.limit!),
					selectedIndex: 0,
				});
			}
		}
	};

	// Ignored via go/ees005
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private handleKeyDown = (e: KeyboardEvent<any>) => {
		const { items, selectedIndex } = this.state;

		if (!items || !items.length) {
			return;
		}

		if (e.key === 'ArrowDown') {
			// down
			e.preventDefault();
			this.setState({
				selectedIndex: (selectedIndex + 1) % items.length,
			});
		} else if (e.key === 'ArrowUp') {
			// up
			e.preventDefault();
			this.setState({
				selectedIndex: selectedIndex > 0 ? selectedIndex - 1 : items.length - 1,
			});
		}
	};

	private handleMouseMove = (objectId: string) => {
		const { items } = this.state;

		if (items) {
			const index = items.findIndex((item) => item.objectId === objectId);
			this.setState({
				selectedIndex: index,
			});
		}
	};

	private renderRecentList = () => {
		const { items, isLoading, selectedIndex } = this.state;
		return (
			<LinkSearchList
				items={items}
				isLoading={isLoading}
				selectedIndex={selectedIndex}
				onSelect={this.handleSelected}
				onMouseMove={this.handleMouseMove}
			/>
		);
	};

	private clearValue = () => {
		this.setState({
			url: '',
		});
	};

	render() {
		const { render } = this.props;
		const { url } = this.state;
		return render({
			activityProvider: this.activityProvider,
			inputProps: {
				onChange: this.handleChange,
				onKeyDown: this.handleKeyDown,
				onSubmit: this.handleSubmit,
				value: url,
			},
			clearValue: this.clearValue,
			currentInputMethod: this.getCurrentInputMethod(),
			renderRecentList: this.renderRecentList,
		});
	}
}

const _default_1: {
	new (
		props: Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
			ExpandedActivityProviderProps,
	): {
		componentDidCatch?: (error: Error, errorInfo: React.ErrorInfo) => void;
		componentDidMount?: () => void;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any
		componentDidUpdate?: (
			prevProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-empty-object-type
			prevState: Readonly<{}>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			snapshot?: any,
		) => void;
		componentWillMount?: () => void;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		componentWillReceiveProps?: (
			nextProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			nextContext: any,
		) => void;
		componentWillUnmount?: () => void;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any
		componentWillUpdate?: (
			nextProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-empty-object-type
			nextState: Readonly<{}>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			nextContext: any,
		) => void;
		context: unknown;
		forceUpdate: (callback?: (() => void) | undefined) => void;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any
		getSnapshotBeforeUpdate?: (
			prevProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-empty-object-type
			prevState: Readonly<{}>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		) => any;
		readonly props: Readonly<
			Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
				ExpandedActivityProviderProps
		>;
		refs: {
			[key: string]: React.ReactInstance;
		};
		render: () => React.JSX.Element;
		renderNode: (providers: Providers) => React.JSX.Element;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type
		setState: <K extends never>(
			state: // eslint-disable-next-line @typescript-eslint/no-empty-object-type
			| {}
				| ((
						// eslint-disable-next-line @typescript-eslint/no-empty-object-type
						prevState: Readonly<{}>,
						props: Readonly<
							Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
								ExpandedActivityProviderProps
						>,
						// eslint-disable-next-line @typescript-eslint/no-empty-object-type
						// eslint-disable-next-line @typescript-eslint/no-empty-object-type
				  ) => {} | Pick<{}, K> | null)
				// eslint-disable-next-line @typescript-eslint/no-empty-object-type
				| Pick<{}, K>
				| null,
			callback?: (() => void) | undefined,
		) => void;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any
		shouldComponentUpdate?: (
			nextProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-empty-object-type
			nextState: Readonly<{}>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			nextContext: any,
		) => boolean;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type
		state: Readonly<{}>;
		UNSAFE_componentWillMount?: () => void;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		UNSAFE_componentWillReceiveProps?: (
			nextProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			nextContext: any,
		) => void;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any
		UNSAFE_componentWillUpdate?: (
			nextProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-empty-object-type
			nextState: Readonly<{}>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			nextContext: any,
		) => void;
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	new (
		props: Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
			ExpandedActivityProviderProps,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		context: any,
	): {
		componentDidCatch?: (error: Error, errorInfo: React.ErrorInfo) => void;
		componentDidMount?: () => void;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any
		componentDidUpdate?: (
			prevProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-empty-object-type
			prevState: Readonly<{}>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			snapshot?: any,
		) => void;
		componentWillMount?: () => void;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		componentWillReceiveProps?: (
			nextProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			nextContext: any,
		) => void;
		componentWillUnmount?: () => void;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any
		componentWillUpdate?: (
			nextProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-empty-object-type
			nextState: Readonly<{}>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			nextContext: any,
		) => void;
		context: unknown;
		forceUpdate: (callback?: (() => void) | undefined) => void;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any
		getSnapshotBeforeUpdate?: (
			prevProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-empty-object-type
			prevState: Readonly<{}>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		) => any;
		readonly props: Readonly<
			Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
				ExpandedActivityProviderProps
		>;
		refs: {
			[key: string]: React.ReactInstance;
		};
		render: () => React.JSX.Element;
		renderNode: (providers: Providers) => React.JSX.Element;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type
		setState: <K extends never>(
			state: // eslint-disable-next-line @typescript-eslint/no-empty-object-type
			| {}
				| ((
						// eslint-disable-next-line @typescript-eslint/no-empty-object-type
						prevState: Readonly<{}>,
						props: Readonly<
							Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
								ExpandedActivityProviderProps
						>,
						// eslint-disable-next-line @typescript-eslint/no-empty-object-type
						// eslint-disable-next-line @typescript-eslint/no-empty-object-type
				  ) => {} | Pick<{}, K> | null)
				// eslint-disable-next-line @typescript-eslint/no-empty-object-type
				| Pick<{}, K>
				| null,
			callback?: (() => void) | undefined,
		) => void;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any
		shouldComponentUpdate?: (
			nextProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-empty-object-type
			nextState: Readonly<{}>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			nextContext: any,
		) => boolean;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type
		state: Readonly<{}>;
		UNSAFE_componentWillMount?: () => void;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		UNSAFE_componentWillReceiveProps?: (
			nextProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			nextContext: any,
		) => void;
		// eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-explicit-any
		UNSAFE_componentWillUpdate?: (
			nextProps: Readonly<
				Diff<RecentSearchProps & WithActivityProviderProps, WithActivityProviderProps> &
					ExpandedActivityProviderProps
			>,
			// eslint-disable-next-line @typescript-eslint/no-empty-object-type
			nextState: Readonly<{}>,
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			nextContext: any,
		) => void;
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	contextType?: React.Context<any> | undefined;
} = withActivityProvider<RecentSearchProps & WithActivityProviderProps>(injectIntl(RecentLink));
export default _default_1;
